/* PasswordChecker auto-generated by npm run generator */
import { FormHelperText, LinearProgress, makeStyles, TextField, Theme, Typography } from "@material-ui/core"
import { computed, observable } from "mobx"
import React from "react"
import { useRootClasses } from "style"
import zxcvbn from "zxcvbn"

const useStyles = makeStyles((theme: Theme) => ({
    progressBar: {
        margin: theme.spacing(1, 0),
    },
}))

class PasswordCheckerState {
    @observable
    password = ""

    @observable
    confirmPassword = ""

    @observable
    passwordScore?: number

    @observable
    passwordFeedback?: zxcvbn.ZXCVBNFeedback

    @observable
    passwordError?: boolean

    @computed
    get strengthWord() {
        switch (this.passwordScore) {
            case 0:
                return "Weak"
            case 1:
                return "Almost"
            case 2:
                return "Good"
            case 3:
                return "Strong"
            case 4:
                return "Strongest"
            default:
                return ""
        }
    }
    @computed
    get invalidPassword() {
        return this.password !== this.confirmPassword || !this.passwordScore || this.passwordError
    }
}

export const usePasswordChecker = (userInputs?: string[]) => {
    const classes = useStyles()
    const rc = useRootClasses()
    const state = React.useRef(new PasswordCheckerState()).current

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        state.password = e.target.value

        const result = zxcvbn(e.target.value, userInputs)
        state.passwordFeedback = result.feedback
        state.passwordScore = result.score
        state.passwordError = state.passwordScore < 2
    }

    return {
        password: state.password,
        invalidPassword: state.invalidPassword,
        // must be exported like this and used like {passwordChecker} to not cause re-renders
        passwordChecker: (
            <>
                <div>
                    <TextField
                        type="password"
                        error={state.passwordError}
                        label="Password"
                        value={state.password}
                        onChange={handlePasswordChange}
                        autoComplete="new-password"
                        fullWidth
                    />
                    <>
                        <LinearProgress
                            className={classes.progressBar}
                            variant="determinate"
                            value={state.passwordScore != null ? ((state.passwordScore + 1) * 100) / 5 : 0}
                            color={state.passwordError ? "secondary" : "primary"}
                            classes={{
                                colorSecondary: rc.bgErrorLight,
                                barColorSecondary: rc.bgError,
                            }}
                        />
                        <Typography variant="overline">{state.strengthWord}</Typography>
                        {state.passwordError && (
                            <FormHelperText error={state.passwordError}>
                                Password strength suggestions:
                                {state.passwordFeedback?.warning}
                                <br />
                                {state.passwordFeedback?.suggestions.map((sug) => (
                                    <React.Fragment key={sug}>
                                        {sug}
                                        <br />
                                    </React.Fragment>
                                ))}
                                {!state.passwordFeedback?.warning && state.passwordFeedback?.suggestions.length === 0 && "Almost..."}
                            </FormHelperText>
                        )}
                    </>
                </div>
                <TextField
                    type="password"
                    label="Confirm password"
                    value={state.confirmPassword}
                    onChange={(e) => (state.confirmPassword = e.target.value)}
                    error={state.password !== state.confirmPassword}
                    helperText={state.password !== state.confirmPassword ? "Passwords must match" : ""}
                    autoComplete="new-password"
                />
            </>
        ),
    }
}
